import { useNavigate } from '@tanstack/react-router'
import * as d3 from 'd3'
import { differenceInMonths, format } from 'date-fns'
import { CornerDownRight, FileSearchIcon, ScaleIcon } from 'lucide-react'
import { useRef, useState } from 'react'

import {
  BORDERED_SURFACE_FILTER,
  Button,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  ScrollFade,
} from '@/components/ui'
import { useReconciliationEntitled } from '@/modules/account'
import { AccountTypeBadge, CYCLE_PARAM } from '@/modules/projects'
import { cn, getProjectPath } from '@/utils'
import {
  BLOCK_HEIGHT,
  COVERAGE_FILL_CLASS,
  COVERAGE_STATUS_LABEL,
  LABEL_WIDTH,
  MARGIN,
  MAX_VISIBLE_ACCOUNT_ROWS,
  MONTH_WIDTH,
  PLAIN_STATUS_LABEL,
  ROW_HEIGHT,
} from './constants'
import { GapEvidence } from './GapEvidence'
import type { CoverageStatus, HoveredData, MonthlyStatus } from './types'
import { useChartData } from './useChartData'
import { useStatementCoverage } from './useStatementCoverage'

export { exportCoverageCSV } from './exportCoverageCSV'
export { exportCoveragePNG } from './exportCoveragePNG'
export { useStatementCoverage } from './useStatementCoverage'

export interface StatementCoverageProps {
  slug: string
  className?: string
}

export const StatementCoverage = ({
  slug,
  className,
}: StatementCoverageProps) => {
  const [hoveredData, setHoveredData] = useState<HoveredData | null>(null)
  const statementCoverage = useStatementCoverage()
  const { timelineWidth, monthScale, axisScale } =
    useChartData(statementCoverage)
  const navigate = useNavigate()
  const isReconciliationEntitled = useReconciliationEntitled()

  // Keep the action popover open while the cursor travels from the cell into it. mouseleave on
  // either side schedules a close; entering the other side cancels it before it fires.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = null
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setHoveredData(null), 150)
  }

  // The cycle in the URL is what pops the reconciliation workspace. A cell with no segment (legacy,
  // or a cycle whose dates were unusable) names none, so it lands on the list.
  const openReconciliationReview = (cell: MonthlyStatus) => {
    setHoveredData(null)
    navigate({
      to: `${getProjectPath({ slug })}/insights/reconciliation`,
      search:
        cell.segmentId != null ? { [CYCLE_PARAM]: cell.segmentId } : undefined,
    })
  }

  if (!statementCoverage || !monthScale) {
    return
  }

  const { accounts, globalMin } = statementCoverage
  const totalHeight = accounts.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom
  const isScrollable = accounts.length > MAX_VISIBLE_ACCOUNT_ROWS
  const maxVisibleHeight =
    MAX_VISIBLE_ACCOUNT_ROWS * ROW_HEIGHT + MARGIN.top + MARGIN.bottom

  const ticks = axisScale!.ticks(d3.timeMonth.every(3)!)

  return (
    <div className={cn('w-full flex flex-col', className)}>
      <ScrollFade
        containerClassName="w-full mb-8"
        style={isScrollable ? { maxHeight: maxVisibleHeight } : undefined}
      >
        {/* One 2D scroll area: vertical for the account rows, horizontal for the
            timeline. The label column is `sticky left-0` (negative margin so it
            overlays the chart's left axis margin instead of pushing it), which
            keeps the horizontal scrollbar at the visible bottom edge and the
            account labels pinned while scrolling right. */}
        <div className="flex w-max">
          <div
            className="sticky left-0 z-10 bg-card pointer-events-none rounded-tl-lg shrink-0"
            style={{
              width: LABEL_WIDTH,
              height: totalHeight + 16,
              marginRight: -LABEL_WIDTH,
            }}
          >
            <div className="pt-4 pl-4" style={{ paddingTop: MARGIN.top + 16 }}>
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className="flex flex-col justify-center items-end text-right pr-3 pointer-events-auto gap-1"
                  style={{ height: ROW_HEIGHT }}
                >
                  {account.parentId ? (
                    <>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground leading-none">
                        <CornerDownRight className="size-3" />
                        {account.id}
                      </span>
                      <span className="text-[10px] text-muted-foreground leading-none">
                        member of {account.parentId}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-foreground leading-none">
                        {account.id}
                      </span>
                      <AccountTypeBadge type={account.type} size="xs" />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div
            className="p-4 relative shrink-0"
            style={{
              width: timelineWidth + MARGIN.left + MARGIN.right,
              height: totalHeight,
            }}
          >
            <svg
              aria-label="Statement coverage chart"
              role="img"
              width={timelineWidth + MARGIN.left + MARGIN.right}
              height={totalHeight}
              className="block"
            >
              <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
                {ticks.map((tick: Date) => {
                  const diff = differenceInMonths(tick, globalMin)
                  const xPos = diff * MONTH_WIDTH

                  const isYearStart = tick.getMonth() === 0
                  return (
                    <g
                      key={tick.toISOString()}
                      transform={`translate(${xPos}, 0)`}
                    >
                      <line
                        y1={-10}
                        y2={accounts.length * ROW_HEIGHT}
                        stroke="currentColor"
                        strokeWidth="1"
                        className={cn(
                          'text-gray-300 dark:text-gray-700',
                          isYearStart ? 'opacity-50' : 'opacity-20',
                        )}
                        strokeDasharray={isYearStart ? '' : '4 4'}
                      />
                      {isYearStart && (
                        <text
                          y={-14}
                          textAnchor="start"
                          className="text-[10px] fill-muted-foreground font-medium"
                        >
                          {format(tick, 'yyyy')}
                        </text>
                      )}
                    </g>
                  )
                })}

                {accounts.map((account, i) => {
                  const yPos = i * ROW_HEIGHT + (ROW_HEIGHT - BLOCK_HEIGHT) / 2

                  return (
                    <g key={account.id} className="group">
                      <rect
                        x={-10}
                        y={i * ROW_HEIGHT}
                        width={timelineWidth + 20}
                        height={ROW_HEIGHT}
                        className="fill-transparent hover:fill-muted/5 transition-colors"
                      />

                      {account.monthlyStatus.map((status) => {
                        const xStart = monthScale(status.date)
                        const width = 16
                        const xOffset = 2

                        const hasNoStatement =
                          status.status === 'missing' ||
                          status.status === 'verified_gap'
                        const finalX =
                          xStart + xOffset + (hasNoStatement ? 0.5 : 0)
                        const finalY = yPos + (hasNoStatement ? 0.5 : 0)
                        const finalW = width - (hasNoStatement ? 1 : 0)
                        const finalH = BLOCK_HEIGHT - (hasNoStatement ? 1 : 0)

                        return (
                          <rect
                            key={status.date.toISOString()}
                            x={finalX}
                            y={finalY}
                            width={finalW}
                            height={finalH}
                            rx={2}
                            shapeRendering="auto"
                            className={cn(
                              'transition-colors shape-rendering-geometricPrecision',
                              COVERAGE_FILL_CLASS[status.status],
                            )}
                            onClick={
                              hasNoStatement
                                ? undefined
                                : () => openReconciliationReview(status)
                            }
                            onMouseEnter={() => {
                              cancelClose()
                              setHoveredData({
                                cell: status,
                                account: account.id,
                                parentAccount: account.parentId,
                                left: 16 + MARGIN.left + finalX,
                                top: 16 + MARGIN.top + finalY,
                                width: finalW,
                                height: finalH,
                              })
                            }}
                            onMouseLeave={scheduleClose}
                          />
                        )
                      })}
                    </g>
                  )
                })}
              </g>
            </svg>

            <Popover open={!!hoveredData}>
              <PopoverAnchor asChild>
                <span
                  className="absolute block pointer-events-none"
                  style={
                    hoveredData
                      ? {
                          left: hoveredData.left,
                          top: hoveredData.top,
                          width: hoveredData.width,
                          height: hoveredData.height,
                        }
                      : undefined
                  }
                />
              </PopoverAnchor>
              {hoveredData && (
                <PopoverContent
                  side="top"
                  sideOffset={10}
                  onOpenAutoFocus={(event) => event.preventDefault()}
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                  style={{ filter: BORDERED_SURFACE_FILTER }}
                  className="w-auto min-w-[200px] overflow-visible border-0 p-0 shadow-none"
                >
                  {/* Invisible strips spanning the sideOffset gap so the hover survives the trip
                      from the cell to the popover. They live in the content subtree, so the cursor
                      over them never fires the content's mouseleave. One covers each side, so the
                      bridge still works when the popover flips below a top-row cell. */}
                  <span
                    aria-hidden
                    className="absolute inset-x-0 top-full h-3"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-x-0 bottom-full h-3"
                  />
                  <CoverageCellActions
                    cell={hoveredData.cell}
                    account={hoveredData.account}
                    parentAccount={hoveredData.parentAccount}
                    isReconciliationEntitled={isReconciliationEntitled}
                    onReviewReconciliation={() =>
                      openReconciliationReview(hoveredData.cell)
                    }
                  />
                  <PopoverArrow
                    width={12}
                    height={6}
                    className="fill-popover"
                  />
                </PopoverContent>
              )}
            </Popover>
          </div>
        </div>
      </ScrollFade>

      <ScrollFade
        containerClassName="border-t"
        style={isScrollable ? { maxHeight: maxVisibleHeight } : undefined}
      >
        {accounts.map((account) => (
          <div
            key={account.id}
            className={cn(
              'flex items-center justify-between px-6 py-3 border-b last:border-0 hover:bg-neutral-50/50',
              account.parentId && 'pl-10',
            )}
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                {account.parentId && (
                  <CornerDownRight className="size-3 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">{account.id}</span>
                {account.parentId ? (
                  <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                    Member card
                  </span>
                ) : (
                  <AccountTypeBadge type={account.type} size="xs" />
                )}
              </div>
              <span className="text-xs text-muted-foreground mt-0.5">
                {account.parentId
                  ? `${account.rangeText} · no separate statement balance`
                  : account.rangeText}
              </span>
            </div>
            <div className="text-xs text-right flex flex-col items-end gap-1">
              {account.parentId ? (
                <span className="text-violet-700 font-medium flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                  Reconciled under account {account.parentId}
                </span>
              ) : account.missingText === 'Complete' ? (
                <span className="text-emerald-600 font-medium flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Complete Coverage
                </span>
              ) : (
                <div className="flex flex-col items-end">
                  <span className="text-red-600 font-medium flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    Potentially Missing Statements
                  </span>
                  <span className="text-muted-foreground text-right max-w-[300px]">
                    {account.missingText}
                  </span>
                </div>
              )}
              {isReconciliationEntitled && (
                <ReconcileSummary counts={account.statusCounts} />
              )}
            </div>
          </div>
        ))}
      </ScrollFade>

      <div className="border-t p-4 bg-muted/5">
        <div className="flex gap-x-6 gap-y-2 text-xs text-muted-foreground justify-center flex-wrap">
          {legendFor(isReconciliationEntitled).map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className={cn('w-3 h-3 rounded-sm', item.swatch)} />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// The hover popover: names the cell's month and reconcile status, then offers to open the cycle
// behind a covered month. A 'missing' month has no statement behind it, so it gets the label alone,
// no action. A member card's cell opens the primary account's cycle, which is where its rows
// reconcile and where the statement pages are.
function CoverageCellActions({
  cell,
  account,
  parentAccount,
  isReconciliationEntitled,
  onReviewReconciliation,
}: {
  cell: MonthlyStatus
  account: string
  parentAccount?: string
  isReconciliationEntitled: boolean
  onReviewReconciliation: () => void
}) {
  const hasNoStatement =
    cell.status === 'missing' || cell.status === 'verified_gap'

  // Prefer the statement's real printed period when we have it, since one cell's month is only where
  // the cycle sits, not its actual span (e.g. Aug 20 - Sep 16). Fall back to the month otherwise.
  const heading =
    cell.periodStart && cell.periodEnd
      ? `${format(cell.periodStart, 'MMM d, yyyy')} - ${format(cell.periodEnd, 'MMM d, yyyy')}`
      : format(cell.date, 'MMMM yyyy')

  const statusLabel = isReconciliationEntitled
    ? COVERAGE_STATUS_LABEL[cell.status]
    : PLAIN_STATUS_LABEL[cell.status]
  const memberSubtitle = `Card ${account} · member of account ${parentAccount}`
  const subtitle = parentAccount
    ? isReconciliationEntitled
      ? `${memberSubtitle} · reconciled under ${parentAccount}`
      : memberSubtitle
    : `Account ${account} · ${statusLabel}`

  return (
    <div className="text-sm">
      <div className="px-3 py-2 border-b">
        <div className="font-semibold leading-tight">{heading}</div>
        <div className="text-muted-foreground text-xs">{subtitle}</div>
      </div>

      {hasNoStatement ? (
        <div className="px-3 py-2 text-muted-foreground max-w-[280px]">
          {isReconciliationEntitled ? (
            <GapEvidence month={cell} />
          ) : (
            <p className="text-xs">No statement to open for this month.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col p-1">
          <Button
            theme="ghost"
            size="sm"
            className="justify-start font-normal"
            onClick={onReviewReconciliation}
          >
            {isReconciliationEntitled ? <ScaleIcon /> : <FileSearchIcon />}
            {isReconciliationEntitled ? 'Review reconciliation' : 'View cycle'}
          </Button>
        </div>
      )}
    </div>
  )
}

// The summary line adds the reconcile verdict, but only when something needs flagging. When every
// covered month reconciled the green cells already say so, so we stay silent to avoid a second line.
function ReconcileSummary({
  counts,
}: {
  counts: Record<CoverageStatus, number>
}) {
  if (counts.needs_review > 0) {
    return (
      <span className="text-amber-600 font-medium flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Needs review ({counts.needs_review})
      </span>
    )
  }
  if (counts.unverifiable > 0) {
    return (
      <span className="text-slate-600 font-medium flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Unverifiable ({counts.unverifiable})
      </span>
    )
  }
  if (counts.reconciled === 0 && counts.not_checked > 0) {
    return (
      <span className="text-blue-600 font-medium flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
        Not checked
      </span>
    )
  }
  return null
}

const MISSING_LEGEND = {
  label: 'No statement (potentially missing)',
  swatch: 'bg-neutral-200 border border-neutral-400/60',
}

const COVERAGE_LEGEND: { label: string; swatch: string }[] = [
  { label: 'Reconciled', swatch: 'bg-emerald-500' },
  { label: 'Needs review', swatch: 'bg-amber-500' },
  { label: 'Unverifiable', swatch: 'bg-slate-400' },
  { label: 'Not checked', swatch: 'bg-blue-500' },
  {
    label: 'Member activity (reconciled under primary)',
    swatch: 'bg-violet-300',
  },
  {
    label: 'No statement (balances verified)',
    swatch: 'bg-emerald-100 border border-emerald-400/60',
  },
  MISSING_LEGEND,
]

// Without the feature a cell can only be covered or missing: every other colour needs a balance read,
// so naming those states would explain swatches that never appear and report a verdict besides.
const PLAIN_LEGEND: { label: string; swatch: string }[] = [
  { label: 'Covered', swatch: 'bg-blue-500' },
  MISSING_LEGEND,
]

const legendFor = (isReconciliationEntitled: boolean) =>
  isReconciliationEntitled ? COVERAGE_LEGEND : PLAIN_LEGEND
